import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import type { EmployeeRow } from '../employeeTypes';
import { EmployeeApiFactory } from '../api/employeeApi';

interface UseGetEmployeeResult {
  data: EmployeeRow | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * `enabled` (default `true`) lets the caller skip the request, e.g. when the profile lacks
 * `employee_record` display (HOMCO-588): the server would answer 403 and `useFetch` would
 * resync the permissions. While disabled, `data` and `error` stay `null`.
 */
export function useGetEmployeeById(
  id: string,
  enabled = true,
): UseGetEmployeeResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => EmployeeApiFactory(client), [client]);

  const [data, setData] = useState<EmployeeRow | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!id || !enabled) {
      // Losing `enabled` after a fetch (permissions refreshed) must not leave the old record behind.
      setData(null);
      setError(null);
      return;
    }

    const ctrl = new AbortController();
    setData(null);
    setError(null);

    api
      .getById(id, { signal: ctrl.signal })
      .then(employee => {
        setData(employee);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setData(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api, id, enabled]);

  return { data, loading, error };
}
